import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
    Shield, 
    Search, 
    ChevronLeft, 
    ChevronRight,
    User,
    FileText,
    DollarSign,
    Upload,
    LogIn,
    Eye,
    Clock,
    Filter,
    Activity,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Loader2
} from 'lucide-react';
import api from '../../lib/api';
import { formatDistanceToNow } from 'date-fns';

const ACTION_ICONS = {
    'USER_LOGIN': LogIn,
    'INVOICE_APPROVE': CheckCircle,
    'INVOICE_APPROVED': CheckCircle,
    'INVOICE_REJECT': XCircle,
    'INVOICE_REJECTED': XCircle,
    'INVOICE_PAID': DollarSign,
    'REVENUE_UPLOAD': Upload,
    'AUDIT_LOG_VIEWED': Eye,
    'LABEL_CREATED': FileText,
    'USER_CREATED': User,
    'REVENUE_SHARE_CHANGED': DollarSign,
};

const ACTION_COLORS = {
    'USER_LOGIN': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'INVOICE_APPROVE': 'bg-green-500/20 text-green-400 border-green-500/30',
    'INVOICE_APPROVED': 'bg-green-500/20 text-green-400 border-green-500/30',
    'INVOICE_REJECT': 'bg-red-500/20 text-red-400 border-red-500/30',
    'INVOICE_REJECTED': 'bg-red-500/20 text-red-400 border-red-500/30',
    'INVOICE_PAID': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'REVENUE_UPLOAD': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'AUDIT_LOG_VIEWED': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'LABEL_CREATED': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'USER_CREATED': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
};

const AuditLogsPage = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        action: '',
        actor_email: '',
        target_type: ''
    });
    const [actions, setActions] = useState([]);

    useEffect(() => {
        fetchAuditLogs();
        fetchSummary();
        fetchActions();
    }, [page, filters]);

    const fetchAuditLogs = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '30'
            });
            
            if (filters.action) params.append('action', filters.action);
            if (filters.actor_email) params.append('actor_email', filters.actor_email);
            if (filters.target_type) params.append('target_type', filters.target_type);
            
            const response = await api.get(`/audit-logs?${params.toString()}`);
            setLogs(response.data.logs || []);
            setTotalPages(response.data.total_pages || 1);
            setTotal(response.data.total || 0);
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const response = await api.get('/audit-logs/summary?days=7');
            setSummary(response.data);
        } catch (error) {
            console.error('Failed to fetch summary:', error);
        }
    };

    const fetchActions = async () => {
        try {
            const response = await api.get('/audit-logs/actions');
            setActions(response.data.actions || []);
        } catch (error) {
            console.error('Failed to fetch actions:', error);
        }
    };

    const formatTimestamp = (timestamp) => {
        try {
            const date = new Date(timestamp);
            return {
                relative: formatDistanceToNow(date, { addSuffix: true }),
                full: date.toLocaleString()
            };
        } catch {
            return { relative: timestamp, full: timestamp };
        }
    };

    const getActionIcon = (action) => {
        const Icon = ACTION_ICONS[action] || Activity;
        return Icon;
    };

    const getActionColor = (action) => {
        return ACTION_COLORS[action] || 'bg-muted text-muted-foreground border-border';
    };

    return (
        <div className="space-y-6" data-testid="audit-logs-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-heading font-bold tracking-tight">Audit Logs</h1>
                        <p className="text-muted-foreground">Immutable system activity records</p>
                    </div>
                </div>
                <Badge variant="outline" className="w-fit bg-amber-500/10 text-amber-500 border-amber-500/30">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Read-Only • Cannot be modified or deleted
                </Badge>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Events (7d)</p>
                                    <p className="text-2xl font-bold">{summary.total_events?.toLocaleString() || 0}</p>
                                </div>
                                <Activity className="w-8 h-8 text-primary opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Unique Actions</p>
                                    <p className="text-2xl font-bold">{summary.by_action?.length || 0}</p>
                                </div>
                                <FileText className="w-8 h-8 text-secondary opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Active Users</p>
                                    <p className="text-2xl font-bold">{summary.by_actor?.length || 0}</p>
                                </div>
                                <User className="w-8 h-8 text-accent opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Critical Actions</p>
                                    <p className="text-2xl font-bold">{summary.recent_critical?.length || 0}</p>
                                </div>
                                <AlertTriangle className="w-8 h-8 text-destructive opacity-50" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card className="bg-card/50 border-border/40">
                <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4 items-center">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Filters:</span>
                        </div>
                        <Select 
                            value={filters.action || "all"} 
                            onValueChange={(val) => {
                                setFilters(f => ({...f, action: val === "all" ? "" : val}));
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="w-48 bg-background">
                                <SelectValue placeholder="Action Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Actions</SelectItem>
                                {actions.map(action => (
                                    <SelectItem key={action} value={action}>{action}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            placeholder="Search by email..."
                            className="w-64 bg-background"
                            value={filters.actor_email}
                            onChange={(e) => {
                                setFilters(f => ({...f, actor_email: e.target.value}));
                                setPage(1);
                            }}
                        />
                        <Select 
                            value={filters.target_type || "all"} 
                            onValueChange={(val) => {
                                setFilters(f => ({...f, target_type: val === "all" ? "" : val}));
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="w-40 bg-background">
                                <SelectValue placeholder="Target Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Targets</SelectItem>
                                <SelectItem value="invoice">Invoice</SelectItem>
                                <SelectItem value="label">Label</SelectItem>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="revenue">Revenue</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="ml-auto text-sm text-muted-foreground">
                            {total.toLocaleString()} total records
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Logs Table */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-heading">Activity Log</CardTitle>
                    <CardDescription>All system actions are permanently recorded</CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>No audit logs found</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {logs.map((log) => {
                                const ActionIcon = getActionIcon(log.action);
                                const time = formatTimestamp(log.timestamp);
                                
                                return (
                                    <div 
                                        key={log.id} 
                                        className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                                    >
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${getActionColor(log.action)}`}>
                                            <ActionIcon className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Badge variant="outline" className={getActionColor(log.action)}>
                                                    {log.action}
                                                </Badge>
                                                <span className="text-sm text-muted-foreground">by</span>
                                                <span className="text-sm font-medium">{log.actor_email}</span>
                                                <Badge variant="outline" className="text-xs">
                                                    {log.actor_role}
                                                </Badge>
                                            </div>
                                            {log.target_name && (
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    Target: <span className="text-foreground">{log.target_name}</span>
                                                    {log.target_type && <span className="text-xs ml-1">({log.target_type})</span>}
                                                </p>
                                            )}
                                            {log.details && Object.keys(log.details).length > 0 && (
                                                <div className="mt-2 text-xs text-muted-foreground bg-muted/50 rounded p-2">
                                                    {Object.entries(log.details).slice(0, 4).map(([key, value]) => (
                                                        <span key={key} className="mr-3">
                                                            <span className="opacity-70">{key}:</span>{' '}
                                                            <span className="text-foreground">
                                                                {typeof value === 'number' ? value.toLocaleString() : String(value)}
                                                            </span>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="text-sm text-muted-foreground" title={time.full}>
                                                {time.relative}
                                            </p>
                                            <p className="text-xs text-muted-foreground/60 mt-1">
                                                {time.full.split(',')[0]}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border/40">
                            <p className="text-sm text-muted-foreground">
                                Page {page} of {totalPages}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AuditLogsPage;
